import { jsx as _jsx } from "react/jsx-runtime";
import { teal } from "@air/colors";
import noop from "lodash/noop";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAnnotationHistory } from "~/components/AnnotationsV2/hooks/useAnnotationHistory";
import { centralizedClipIdSelector } from "~/store/centralizedClip/selectors";
const defaultValue = {
    annotationType: "box",
    annotationColor: teal.teal7.hex,
    annotationSize: 3,
    isAnnotationsEnabled: false,
    annotations: [],
    isDrawing: false,
    selectedTool: "point",
    activeDiscussion: null,
    setAnnotationType: noop,
    setAnnotationColor: noop,
    setAnnotationSize: noop,
    setAnnotationsEnabled: noop,
    setAnnotations: noop,
    setIsDrawing: noop,
    setSelectedTool: noop,
    setActiveDiscussion: noop,
    undo: noop,
    redo: noop,
    canUndo: false,
    canRedo: false,
    clearHistory: noop
};
const AnnotationV2Context = /*#__PURE__*/ createContext(defaultValue);
const initialState = {
    annotationType: "box",
    annotationColor: teal.teal7.hex,
    annotationSize: 3,
    isAnnotationsEnabled: false,
    annotations: [],
    isDrawing: false,
    selectedTool: "point"
};
export const AnnotationV2Provider = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    const centralizedClipId = useSelector(centralizedClipIdSelector);
    // Use history hook for undoable state (track annotations)
    const { state: historyState, setState: setHistoryState, undo, redo, canUndo, canRedo, clearHistory } = useAnnotationHistory({
        annotations: initialState.annotations
    });
    // Non-undoable state (UI state that shouldn't be part of undo/redo)
    const [sessionState, setSessionState] = useState({
        annotationType: initialState.annotationType,
        annotationColor: initialState.annotationColor,
        annotationSize: initialState.annotationSize,
        isAnnotationsEnabled: initialState.isAnnotationsEnabled,
        isDrawing: initialState.isDrawing,
        selectedTool: initialState.selectedTool
    });
    // Active discussion state (separate from history)
    const [activeDiscussion, setActiveDiscussion] = useState(null);
    // Clear history when clip changes
    useEffect(()=>{
        clearHistory();
        setHistoryState({
            annotations: []
        });
        setSessionState({
            annotationType: "box",
            annotationColor: teal.teal7.hex,
            annotationSize: 3,
            isAnnotationsEnabled: false,
            isDrawing: false,
            selectedTool: "point"
        });
        // Clear active discussion when clip changes
        setActiveDiscussion(null);
    }, [
        centralizedClipId,
        clearHistory,
        setHistoryState
    ]);
    // Actions for session state
    const setAnnotationType = useCallback((type)=>{
        setSessionState((prev)=>({
                ...prev,
                annotationType: type
            }));
    }, []);
    const setAnnotationColor = useCallback((color)=>{
        setSessionState((prev)=>({
                ...prev,
                annotationColor: color
            }));
    }, []);
    const setAnnotationSize = useCallback((size)=>{
        setSessionState((prev)=>({
                ...prev,
                annotationSize: size
            }));
    }, []);
    const setAnnotationsEnabled = useCallback((enabled)=>{
        setSessionState((prev)=>({
                ...prev,
                isAnnotationsEnabled: enabled
            }));
    }, []);
    const setIsDrawing = useCallback((isDrawing)=>{
        setSessionState((prev)=>({
                ...prev,
                isDrawing
            }));
    }, []);
    const setSelectedTool = useCallback((tool)=>{
        setSessionState((prev)=>({
                ...prev,
                selectedTool: tool
            }));
    }, []);
    // Actions for history state (these will be tracked for undo/redo)
    const setAnnotations = useCallback((annotations)=>{
        setHistoryState({
            annotations
        });
    }, [
        setHistoryState
    ]);
    const value = useMemo(()=>({
            // Combine history and session state
            ...sessionState,
            ...historyState,
            activeDiscussion,
            // Actions
            setAnnotationType,
            setAnnotationColor,
            setAnnotationSize,
            setAnnotationsEnabled,
            setAnnotations,
            setIsDrawing,
            setSelectedTool,
            setActiveDiscussion,
            // History actions
            undo,
            redo,
            canUndo,
            canRedo,
            clearHistory
        }), [
        sessionState,
        historyState,
        activeDiscussion,
        setAnnotationType,
        setAnnotationColor,
        setAnnotationSize,
        setAnnotationsEnabled,
        setAnnotations,
        setIsDrawing,
        setSelectedTool,
        setActiveDiscussion,
        undo,
        redo,
        canUndo,
        canRedo,
        clearHistory
    ]);
    return /*#__PURE__*/ _jsx(AnnotationV2Context.Provider, {
        value: value,
        children: children
    });
});
AnnotationV2Provider.displayName = "AnnotationV2Provider";
export function useAnnotationV2Context() {
    const context = useContext(AnnotationV2Context);
    if (context === defaultValue) {
        throw new Error("useAnnotationV2Context must be used within AnnotationV2Provider");
    }
    return context;
}
